import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import {
  Button,
  Grid,
  Typography,
  Card,
  TextField,
  CircularProgress,
  MenuItem,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import { deviceDetect } from 'react-device-detect'
import ThumbUpOnAltIcon from '@material-ui/icons/RateReview'

import Modal from '../../../../../../CommonComponents/Modal/Modal'
import { WhatsNewFeedbackStyles } from './WhatsNewFeedbackStyles'
import {
  sendFeedback,
  selectUserData,
} from '../../../../../settings/settingsSlice'

function FooterButton({ onLeaveFeedbackClick, edited }) {
  return (
    <Button
      onClick={onLeaveFeedbackClick}
      variant="outlined"
      disabled={!edited}
      color="primary"
    >
      Leave Feedback
    </Button>
  )
}

function WhatsNewFeedback({ incomingTitle, isLarge = false }) {
  // Parent component that manages state for all it's children
  const dispatch = useDispatch()
  const classes = WhatsNewFeedbackStyles()
  // modal logic
  const [edited, setEdited] = React.useState(false)
  const [modalOpen, setModalOpen] = React.useState(false)
  // form logic
  const TITLE_LIMIT = 50
  const DESCRIPTION_LIMIT = 600
  const userData = useSelector(selectUserData)
  const [focusedTitle, setFocusedTitle] = React.useState(false)
  const [focusedDescription, setFocusedDescription] = React.useState(false)
  const [colorTitle, setColorTitle] = React.useState('')
  const [colorDesc, setColorDesc] = React.useState('')
  const [titleCharCount, setTitleCharCount] = React.useState(0)
  const [descriptionCharCount, setDescriptionCharCount] = React.useState(0)
  const [feedbackCategory, setFeedback] = React.useState('Enhancement')
  const [title, setTitle] = React.useState(incomingTitle)
  const [description, setDescription] = React.useState('')

  // submit logic
  const [alert, setAlert] = React.useState('default')
  const [showAlert, setShowAlert] = React.useState(false)
  const [showSubmitting, setShowSubmitting] = React.useState(false)

  const feedbackOptions = [
    {
      value: 'Enhancement',
      label: 'Enhancement',
    },
    {
      value: 'Issue',
      label: 'Issue',
    },
    {
      value: 'General',
      label: 'General',
    },
  ]

  const setOpen = () => {
    if (modalOpen) {
      setModalOpen(false)
    } else {
      setModalOpen(true)
    }
  }

  const handleClose = () => {
    setEdited(false)
    setDescription('')
    setOpen()
  }

  const handleFocusTitle = () => {
    setFocusedTitle(true)
  }
  const handleBlurTitle = () => {
    setFocusedTitle(false)
  }
  const handleFocusDesc = () => {
    setFocusedDescription(true)
  }
  const handleBlurDesc = () => {
    setFocusedDescription(false)
  }

  const handleFeedbackChange = (event) => {
    setFeedback(event.target.value)
  }

  const handleTitleChange = (event) => {
    setTitle(event.target.value.substring(0, TITLE_LIMIT))
    setTitleCharCount(event.target.value.substring(0, TITLE_LIMIT).length)

    if (event.target.value.length >= TITLE_LIMIT - 5) setColorTitle('primary')
    else setColorTitle('')
  }

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value.substring(0, DESCRIPTION_LIMIT))
    setDescriptionCharCount(
      event.target.value.substring(0, DESCRIPTION_LIMIT).length,
    )
    if (event.target.value.length > 0) setEdited(true)
    else setEdited(false)
    if (event.target.value.length >= DESCRIPTION_LIMIT - 5)
      setColorDesc('primary')
    else setColorDesc('')
  }

  const handleSubmit = () => {
    setShowSubmitting(true)
    setShowAlert(false)
    const email = userData ? userData.email : 'error'
    const usersDevice = deviceDetect()
    const device = `${usersDevice.osName} ${usersDevice.osVersion}`
    const osversion = `${usersDevice.browserName} ${usersDevice.browserMajorVersion}`
    const body = {
      appversion: 'Web',
      category: feedbackCategory,
      date: moment().format('MMMM Do YYYY, h:mm:ss a'),
      description,
      device,
      email,
      osversion,
      title,
    }
    dispatch(sendFeedback(body))
      .then(() => {
        setShowAlert(true)
        setShowSubmitting(false)
        setAlert({
          severity: 'success',
          title: 'Success',
          message: 'Your feedback has been sent!',
        })
        setTitle('')
        setDescription('')
      })
      .catch(() => {
        setShowAlert(true)
        setShowSubmitting(false)
        setAlert({
          severity: 'error',
          title: 'Error',
          message:
            'Something went wrong submitting your feedback. Please try again.',
        })
        setTitle('')
        setDescription('')
      })
  }

  const onLeaveFeedbackClick = () => {
    handleSubmit()
    handleClose()
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpen()}
        className={
          isLarge ? classes.feedbackButtonLarge : classes.feedbackButton
        }
      >
        <ThumbUpOnAltIcon />
        &nbsp;Feedback
      </Button>
      <Modal
        open={modalOpen}
        dialogTitle="Leave Feedback"
        onCloseDialog={handleClose}
        dialogSubtitle={null}
        dialogContent={
          <WhatsNewFeedbackContent
            classes={classes}
            incomingTitle={incomingTitle}
            handleFocusTitle={handleFocusTitle}
            handleBlurTitle={handleBlurTitle}
            handleFocusDesc={handleFocusDesc}
            handleBlurDesc={handleBlurDesc}
            handleFeedbackChange={handleFeedbackChange}
            handleTitleChange={handleTitleChange}
            handleDescriptionChange={handleDescriptionChange}
            TITLE_LIMIT={TITLE_LIMIT}
            DESCRIPTION_LIMIT={DESCRIPTION_LIMIT}
            title={title}
            description={description}
            titleCharCount={titleCharCount}
            descriptionCharCount={descriptionCharCount}
            colorTitle={colorTitle}
            colorDesc={colorDesc}
            feedbackCategory={feedbackCategory}
            feedbackOptions={feedbackOptions}
            showAlert={showAlert}
            setShowAlert={setShowAlert}
            showSubmitting={showSubmitting}
            focusedTitle={focusedTitle}
            focusedDescription={focusedDescription}
            alert={alert}
            setShowSubmitting={setShowSubmitting}
            setAlert={setAlert}
            setTitle={setTitle}
            setDescription={setDescription}
            setColorTitle={setColorTitle}
            setColorDesc={setColorDesc}
            setTitleCharCount={setTitleCharCount}
            setDescriptionCharCount={setDescriptionCharCount}
            setFeedback={setFeedback}
          />
        }
        dialogActions={
          <FooterButton
            onLeaveFeedbackClick={onLeaveFeedbackClick}
            edited={edited}
          />
        }
      />
    </>
  )
}

function FeedbackCard({
  classes,
  title,
  handleFocusTitle,
  handleBlurTitle,
  handleFocusDesc,
  handleBlurDesc,
  handleFeedbackChange,
  handleTitleChange,
  handleDescriptionChange,
  TITLE_LIMIT,
  DESCRIPTION_LIMIT,
  description,
  titleCharCount,
  descriptionCharCount,
  colorTitle,
  colorDesc,
  feedbackCategory,
  feedbackOptions,
  showAlert,
  setShowAlert,
  showSubmitting,
  focusedTitle,
  focusedDescription,
}) {
  return (
    <Card className={classes.linksCard}>
      {showAlert ? (
        <Alert
          severity={alert.severity}
          onClose={() => {
            setShowAlert(false)
          }}
        >
          <AlertTitle>{alert.title}</AlertTitle>
          {alert.message}
        </Alert>
      ) : (
        <> </>
      )}
      {showSubmitting ? (
        <CircularProgress />
      ) : (
        <form autoComplete="off" className={classes.feedbackFormContainer}>
          <TextField
            id="feedback-option"
            select
            label="Category"
            variant="outlined"
            value={feedbackCategory}
            onChange={handleFeedbackChange}
            fullWidth
            margin="normal"
          >
            {feedbackOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            disabled
            id="descriptive-title"
            label="Title"
            placeholder="Title"
            variant="outlined"
            fullWidth
            margin="normal"
            value={title}
            onChange={handleTitleChange}
            onFocus={handleFocusTitle}
            onBlur={handleBlurTitle}
          />
          {focusedTitle && titleCharCount / TITLE_LIMIT >= 0.8 ? (
            <Typography variant="body2" color={colorTitle}>
              {TITLE_LIMIT - titleCharCount}
            </Typography>
          ) : (
            <></>
          )}
          <TextField
            className={classes.descriptionField}
            id="descriptive-issue"
            label="Description"
            placeholder="Description"
            variant="outlined"
            multiline
            fullWidth
            margin="normal"
            minRows={8}
            value={description}
            onChange={handleDescriptionChange}
            onFocus={handleFocusDesc}
            onBlur={handleBlurDesc}
          />
          {focusedDescription &&
          descriptionCharCount / DESCRIPTION_LIMIT >= 0.8 ? (
            <Typography variant="body2" color={colorDesc}>
              {DESCRIPTION_LIMIT - descriptionCharCount}
            </Typography>
          ) : (
            <></>
          )}
        </form>
      )}
    </Card>
  )
}

function WhatsNewFeedbackContent({
  classes,
  incomingTitle,
  handleFocusTitle,
  handleBlurTitle,
  handleFocusDesc,
  handleBlurDesc,
  handleFeedbackChange,
  handleTitleChange,
  handleDescriptionChange,
  TITLE_LIMIT,
  DESCRIPTION_LIMIT,
  description,
  titleCharCount,
  descriptionCharCount,
  colorTitle,
  colorDesc,
  feedbackCategory,
  feedbackOptions,
  showAlert,
  setShowAlert,
  showSubmitting,
  focusedTitle,
  focusedDescription,
}) {
  return (
    <Grid
      container
      alignContent="center"
      alignItems="center"
      justifyContent="center"
      justifyItems="center"
      className={classes.gridContainer}
    >
      <Grid item xs={12} className={classes.gridItem}>
        <FeedbackCard
          classes={classes}
          title={incomingTitle}
          handleFocusTitle={handleFocusTitle}
          handleBlurTitle={handleBlurTitle}
          handleFocusDesc={handleFocusDesc}
          handleBlurDesc={handleBlurDesc}
          handleFeedbackChange={handleFeedbackChange}
          handleTitleChange={handleTitleChange}
          handleDescriptionChange={handleDescriptionChange}
          TITLE_LIMIT={TITLE_LIMIT}
          DESCRIPTION_LIMIT={DESCRIPTION_LIMIT}
          description={description}
          titleCharCount={titleCharCount}
          descriptionCharCount={descriptionCharCount}
          colorTitle={colorTitle}
          colorDesc={colorDesc}
          feedbackCategory={feedbackCategory}
          feedbackOptions={feedbackOptions}
          showAlert={showAlert}
          setShowAlert={setShowAlert}
          showSubmitting={showSubmitting}
          focusedTitle={focusedTitle}
          focusedDescription={focusedDescription}
        />
      </Grid>
    </Grid>
  )
}

export default WhatsNewFeedback
export { FooterButton }
